import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashLayout from '../../components/DashLayout';

const nav = [
  { to: '/dashboard', icon: '📊', label: 'Dashboard' },
  { to: '/dashboard/chats', icon: '💬', label: 'Live Chats' },
  { to: '/dashboard/leads', icon: '👥', label: 'Leads' },
  { to: '/dashboard/crm', icon: '📋', label: 'CRM' },
  { to: '/dashboard/agents', icon: '👤', label: 'Agents' },
  { to: '/dashboard/tickets', icon: '🎫', label: 'Support Tickets' },
  { to: '/dashboard/ai-bot', icon: '🤖', label: 'AI Chat Bot' },
  { to: '/dashboard/api', icon: '🔑', label: 'API Access' },
  { to: '/dashboard/branding', icon: '🎨', label: 'Branding' },
  { to: '/dashboard/widget', icon: '🔌', label: 'Widget & Script' },
  { to: '/dashboard/contact-form', icon: '📝', label: 'Contact Form' },
  { to: '/dashboard/analytics', icon: '📈', label: 'Analytics' },
  { to: '/dashboard/billing', icon: '💳', label: 'Billing' },
  { to: '/dashboard/settings', icon: '⚙️', label: 'Settings' },
];

const STATUSES = ['New', 'Contacted', 'Qualified', 'Closed'];
const TAGS = ['Hot 🔥', 'Warm 🌡️', 'Cold ❄️'];
const STATUS_COLORS = { New: '#555', Contacted: '#3b82f6', Qualified: '#f59e0b', Closed: '#00d55b' };

// ── CSV helpers ────────────────────────────────────────────────────────────────

const CSV_HEADERS = ['name', 'phone', 'email', 'company', 'crm_status', 'crm_tag', 'lead_source', 'follow_up_date', 'notes'];

function contactsToCSV(contacts) {
  const header = CSV_HEADERS.join(',');
  const rows = contacts.map(c => {
    const notes = Array.isArray(c.notes) ? c.notes.map(n => n.note).join(' | ') : '';
    return CSV_HEADERS.map(h => {
      const val = h === 'notes' ? notes : (c[h] ?? '');
      const str = String(val).replace(/"/g, '""');
      return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str}"` : str;
    }).join(',');
  });
  return [header, ...rows].join('\n');
}

function parseCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/^"|"$/g, ''));
  return lines.slice(1).map(line => {
    const values = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') { inQ = !inQ; continue; }
      if (line[i] === ',' && !inQ) { values.push(cur); cur = ''; continue; }
      cur += line[i];
    }
    values.push(cur);
    const row = {};
    headers.forEach((h, i) => { row[h] = (values[i] || '').trim(); });
    return row;
  }).filter(r => r.name || r.email || r.phone);
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function CRM() {
  const [contacts,      setContacts]      = useState([]);
  const [selected,      setSelected]      = useState(null);
  const [search,        setSearch]        = useState('');
  const [statusFilter,  setStatusFilter]  = useState('all');
  const [note,          setNote]          = useState('');
  const [loading,       setLoading]       = useState(true);
  const [followUpDate,  setFollowUpDate]  = useState('');
  const [importing,     setImporting]     = useState(false);
  const [importPreview, setImportPreview] = useState(null); // { rows, fileName }
  const fileRef = useRef(null);

  useEffect(() => { loadContacts(); }, []);

  const loadContacts = () => {
    axios.get('/api/business/crm').then(r => {
      setContacts(r.data.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const updateContact = async (id, updates) => {
    await axios.put(`/api/business/crm/${id}`, updates);
    loadContacts();
    if (selected?.id === id) setSelected(prev => ({ ...prev, ...updates }));
  };

  const saveFollowUpDate = async () => {
    if (!selected) return;
    await axios.put(`/api/business/crm/${selected.id}`, { follow_up_date: followUpDate || null });
    setSelected(prev => ({ ...prev, follow_up_date: followUpDate }));
    loadContacts();
    toast.success('Follow-up date saved!');
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await axios.post(`/api/business/crm/${selected.id}/notes`, { note });
      toast.success('Note added!');
      setNote('');
      const r = await axios.get(`/api/business/crm/${selected.id}`);
      if (r.data.data) setSelected(r.data.data);
    } catch {
      toast.error('Failed to add note');
    }
  };

  // ── Export CSV ───────────────────────────────────────────────────────────────
  const exportCSV = async () => {
    try {
      // Use server-side export for notes support
      const res = await axios.get('/api/business/crm/export-csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported!');
    } catch {
      // Fallback: client-side export
      const csv = contactsToCSV(contacts);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported!');
    }
  };

  // ── Import CSV ───────────────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const rows = parseCSV(ev.target.result);
      if (rows.length === 0) { toast.error('No valid contacts found in CSV'); return; }
      setImportPreview({ rows, fileName: file.name });
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const confirmImport = async () => {
    if (!importPreview) return;
    setImporting(true);
    try {
      const res = await axios.post('/api/business/crm/import-csv', { contacts: importPreview.rows });
      toast.success(`${res.data.imported} contacts imported!`);
      setImportPreview(null);
      loadContacts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const filtered = contacts.filter(c => {
    const matchSearch =
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase()) ||
      c.phone?.includes(search);
    const matchStatus = statusFilter === 'all' || c.crm_status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <DashLayout navItems={nav} role="Business Admin">
      <div style={{ padding: 28, display: 'grid', gridTemplateColumns: selected ? '1fr 380px' : '1fr', gap: 20 }}>

        {/* ── Left — Contact list ─────────────────────────────────────────── */}
        <div>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 700 }}>CRM</h1>
              <p style={{ color: '#555', fontSize: 13, marginTop: 4 }}>Manage your customer relationships</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, color: '#555' }}>
                Total: <span style={{ color: '#00d55b', fontWeight: 700 }}>{contacts.length}</span>
              </span>
              {/* Import CSV */}
              <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileSelect} />
              <button onClick={() => fileRef.current?.click()}
                style={{ padding: '8px 14px', background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 8, color: '#fff', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                📥 Import CSV
              </button>
              {/* Export CSV */}
              <button onClick={exportCSV}
                style={{ padding: '8px 14px', background: '#00d55b', border: 'none', borderRadius: 8, color: '#000', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                📤 Export CSV
              </button>
            </div>
          </div>

          {/* CSV column hint */}
          <div style={{ background: 'rgba(0,213,91,0.06)', border: '1px solid rgba(0,213,91,0.15)', borderRadius: 8, padding: '8px 14px', marginBottom: 14, fontSize: 12, color: '#555' }}>
            📄 CSV columns: <span style={{ color: '#00d55b' }}>name, phone, email, company, crm_status, crm_tag, lead_source, follow_up_date, notes</span>
          </div>

          {/* Import preview modal */}
          {importPreview && (
            <div style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: 12, padding: 18, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Preview: {importPreview.fileName}</div>
                  <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{importPreview.rows.length} contacts found</div>
                </div>
                <button onClick={() => setImportPreview(null)}
                  style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 18 }}>✕</button>
              </div>
              <div style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1e1e1e' }}>
                      {['name', 'phone', 'email', 'company', 'crm_status'].map(h => (
                        <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: '#555', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.rows.slice(0, 10).map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #0d0d0d' }}>
                        {['name', 'phone', 'email', 'company', 'crm_status'].map(h => (
                          <td key={h} style={{ padding: '6px 10px', color: '#aaa', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r[h] || '—'}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {importPreview.rows.length > 10 && (
                  <div style={{ textAlign: 'center', padding: '8px', color: '#555', fontSize: 11 }}>
                    +{importPreview.rows.length - 10} more rows
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={confirmImport} disabled={importing}
                  style={{ flex: 1, padding: '10px', background: '#00d55b', border: 'none', borderRadius: 8, color: '#000', fontWeight: 700, fontSize: 13, cursor: importing ? 'not-allowed' : 'pointer', opacity: importing ? 0.7 : 1 }}>
                  {importing ? 'Importing...' : `Import ${importPreview.rows.length} Contacts`}
                </button>
                <button onClick={() => setImportPreview(null)}
                  style={{ padding: '10px 18px', background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 8, color: '#fff', fontSize: 13, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, phone..."
              style={{ padding: '8px 14px', background: '#111', border: '1px solid #1e1e1e', borderRadius: 8, color: '#fff', fontSize: 13, flex: 1, outline: 'none' }} />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              style={{ padding: '8px 14px', background: '#111', border: '1px solid #1e1e1e', borderRadius: 8, color: '#fff', fontSize: 13 }}>
              <option value="all">All Status</option>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Contact list */}
          {loading ? (
            <div style={{ textAlign: 'center', color: '#555', padding: 40 }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#555', padding: 40 }}>No contacts found</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map(contact => (
                <div key={contact.id} onClick={() => { setSelected(contact); setFollowUpDate(contact.follow_up_date ? contact.follow_up_date.slice(0, 10) : ''); }}
                  style={{ background: selected?.id === contact.id ? 'rgba(0,213,91,0.05)' : '#111', border: `1px solid ${selected?.id === contact.id ? '#00d55b' : '#1e1e1e'}`, borderRadius: 12, padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(0,213,91,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#00d55b', flexShrink: 0 }}>
                    {contact.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{contact.name || '—'}</div>
                    <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>
                      {contact.email && <span>📧 {contact.email}</span>}
                      {contact.phone && <span style={{ marginLeft: 8 }}>📞 {contact.phone}</span>}
                    </div>
                    {contact.company && (
                      <div style={{ fontSize: 11, color: '#444', marginTop: 2 }}>🏢 {contact.company}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    {contact.crm_tag && (
                      <span style={{ fontSize: 11, padding: '2px 8px', background: '#1a1a1a', borderRadius: 20 }}>{contact.crm_tag}</span>
                    )}
                    {contact.lead_source && (
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20,
                        background: contact.lead_source === 'form' ? 'rgba(59,130,246,0.15)' : contact.lead_source === 'ai_bot' ? 'rgba(139,92,246,0.15)' : contact.lead_source === 'import' ? 'rgba(245,158,11,0.15)' : 'rgba(0,213,91,0.1)',
                        color:      contact.lead_source === 'form' ? '#3b82f6'              : contact.lead_source === 'ai_bot' ? '#8b5cf6'              : contact.lead_source === 'import' ? '#f59e0b'              : '#00d55b' }}>
                        {contact.lead_source === 'form' ? '📝 Form' : contact.lead_source === 'ai_bot' ? '🤖 AI Bot' : contact.lead_source === 'import' ? '📥 Import' : '💬 Chat'}
                      </span>
                    )}
                    <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: `${STATUS_COLORS[contact.crm_status || 'New']}22`, color: STATUS_COLORS[contact.crm_status || 'New'], fontWeight: 600 }}>
                      {contact.crm_status || 'New'}
                    </span>
                    <span style={{ fontSize: 12, color: '#555' }}>{contact.chat_count || 0} chats</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Right — Contact detail ──────────────────────────────────────────── */}
        {selected && (
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 20, height: 'fit-content', position: 'sticky', top: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700 }}>Contact Details</h3>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>

            {/* Avatar + name */}
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(0,213,91,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700, color: '#00d55b', margin: '0 auto 8px' }}>
                {selected.name?.[0]?.toUpperCase() || '?'}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{selected.name}</div>
              {selected.company && (
                <div style={{ fontSize: 12, color: '#00d55b', marginTop: 2 }}>🏢 {selected.company}</div>
              )}
              <div style={{ fontSize: 12, color: '#555' }}>{selected.city && selected.country ? `${selected.city}, ${selected.country}` : ''}</div>
            </div>

            {/* Contact info */}
            <div style={{ background: '#0d0d0d', borderRadius: 10, padding: 14, marginBottom: 14 }}>
              {selected.email  && <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>📧 {selected.email}</div>}
              {selected.phone  && <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>📞 {selected.phone}</div>}
              {selected.company && <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>🏢 {selected.company}</div>}
              <div style={{ fontSize: 13, color: '#888' }}>💬 {selected.chat_count || 0} total chats</div>
            </div>

            {/* Quick actions */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {selected.phone && <a href={`tel:${selected.phone}`} style={{ flex: 1, textAlign: 'center', padding: '8px', background: 'rgba(0,213,91,0.1)', border: '1px solid rgba(0,213,91,0.3)', borderRadius: 8, fontSize: 18, textDecoration: 'none' }}>📞</a>}
              {selected.email && <a href={`mailto:${selected.email}`} style={{ flex: 1, textAlign: 'center', padding: '8px', background: 'rgba(0,100,255,0.1)', border: '1px solid rgba(0,100,255,0.3)', borderRadius: 8, fontSize: 18, textDecoration: 'none' }}>📧</a>}
              {selected.phone && <a href={`https://wa.me/${selected.phone?.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" style={{ flex: 1, textAlign: 'center', padding: '8px', background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)', borderRadius: 8, fontSize: 18, textDecoration: 'none' }}>💬</a>}
            </div>

            {/* Status */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Status</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {STATUSES.map(s => (
                  <button key={s} onClick={() => updateContact(selected.id, { crm_status: s })}
                    style={{ padding: '5px 12px', borderRadius: 20, border: `1px solid ${selected.crm_status === s ? STATUS_COLORS[s] : '#1e1e1e'}`, background: selected.crm_status === s ? `${STATUS_COLORS[s]}22` : '#0d0d0d', color: selected.crm_status === s ? STATUS_COLORS[s] : '#555', fontSize: 12, cursor: 'pointer' }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Tag */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Tag</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {TAGS.map(t => (
                  <button key={t} onClick={() => updateContact(selected.id, { crm_tag: selected.crm_tag === t ? null : t })}
                    style={{ padding: '5px 10px', borderRadius: 20, border: `1px solid ${selected.crm_tag === t ? '#00d55b' : '#1e1e1e'}`, background: selected.crm_tag === t ? 'rgba(0,213,91,0.1)' : '#0d0d0d', color: selected.crm_tag === t ? '#00d55b' : '#555', fontSize: 12, cursor: 'pointer' }}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Follow-up date */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Follow-up Date</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)}
                  style={{ flex: 1, padding: '8px 12px', background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 8, color: '#fff', fontSize: 13 }} />
                <button onClick={saveFollowUpDate}
                  style={{ padding: '8px 14px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 12, border: 'none', cursor: 'pointer' }}>
                  Save
                </button>
              </div>
              {selected.follow_up_date && (
                <div style={{ fontSize: 11, color: '#00d55b', marginTop: 4 }}>
                  Saved: {new Date(selected.follow_up_date).toLocaleDateString('en-IN')}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Add Note</label>
              <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Type a note..." rows={3}
                style={{ width: '100%', background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 8, color: '#fff', fontSize: 13, padding: '8px 12px', resize: 'none', outline: 'none', fontFamily: 'inherit' }} />
              <button onClick={addNote}
                style={{ width: '100%', marginTop: 8, padding: '9px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer' }}>
                Add Note
              </button>

              {selected.notes && selected.notes.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Previous Notes</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 150, overflowY: 'auto' }}>
                    {selected.notes.map((n, i) => (
                      <div key={i} style={{ background: '#0d0d0d', borderRadius: 8, padding: '8px 12px' }}>
                        <div style={{ fontSize: 13, color: '#ddd', lineHeight: 1.5 }}>{n.note}</div>
                        <div style={{ fontSize: 10, color: '#444', marginTop: 4 }}>{new Date(n.created_at).toLocaleString('en-IN')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashLayout>
  );
}